import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';

export interface QrYazdirEtiket {
    /** Büyük başlık: nokta adı ya da plaka. */
    baslik: string
    /** QR'ın içine yazılan metin. */
    qrMetni: string
    /** Elle girilebilen kısa kod. */
    qrKodu: string
    /** Depo QR'ında iş tipi: etiketin altında büyük harfle ("LOJİSTİK" / "AMBALAJ"), QR Mesai Faz 2. */
    isTipi?: string | null
    /** Etiket çerçeve rengi (Lojistik mavi, Ambalaj turuncu). Verilmezse kesik gri çizgi. */
    renk?: string | null
}

const kacis = (s: string | null | undefined) => (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** A4'e 2 sütun × 4 satır = sayfa başına 8 etiket. */
const SAYFA_BASINA = 8;

const stil = `
    @page { size: A4 portrait; margin: 10mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; color: #000; }
    .sayfa { width: 190mm; height: 277mm; display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: repeat(4, 1fr); gap: 0; page-break-after: always; break-after: page; }
    .sayfa:last-child { page-break-after: auto; break-after: auto; }
    .etiket { border: 0.3mm dashed #999; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 3mm; overflow: hidden; page-break-inside: avoid; break-inside: avoid; }
    .baslik { font-size: 15pt; font-weight: bold; text-align: center; line-height: 1.15; max-height: 12mm; overflow: hidden; margin-bottom: 2mm; }
    .qr svg { width: 45mm; height: 45mm; display: block; }
    .kod { font-size: 14pt; font-weight: bold; letter-spacing: 1.5mm; margin-top: 2mm; }
    .alt { font-size: 7pt; color: #555; margin-top: 1mm; }
    .etiket.renkli { border-style: solid; border-width: 1.2mm; }
    .istipi { font-size: 16pt; font-weight: 800; letter-spacing: 1mm; margin-top: 1mm; }
    .etiket.bos { border: 0; }
    .etiket.istipli .qr svg { width: 38mm; height: 38mm; }
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

const etiketHtml = (e: QrYazdirEtiket) => {
    if (!e.qrMetni)
        return '<div class="etiket bos"></div>';

    const svg = renderToStaticMarkup(<QRCodeSVG value={e.qrMetni} size={256} level='M' includeMargin={false} />);
    const renk = e.renk ? kacis(e.renk) : '';

    return `<div class="etiket${renk ? ' renkli' : ''}${e.isTipi ? ' istipli' : ''}"${renk ? ` style="border-color:${renk}"` : ''}>
        <div class="baslik">${kacis(e.baslik)}</div>
        <div class="qr">${svg}</div>
        ${e.isTipi ? `<div class="istipi"${renk ? ` style="color:${renk}"` : ''}>${kacis(e.isTipi.toLocaleUpperCase('tr-TR'))}</div>` : ''}
        <div class="kod">${kacis(e.qrKodu)}</div>
        <div class="alt">Keylogx · Mesai QR</div>
    </div>`;
}

/**
 * QR etiketlerini A4'e (2 × 4) dizip yazdırma penceresini açar (11.10.2026).
 * Gizli bir iframe'e yazılır ve onun print()'i çağrılır; sayfa değişmez, açılır pencere engeline takılmaz.
 */
export const qrYazdir = (etiketler: QrYazdirEtiket[]) => {
    if (etiketler.length == 0)
        return;

    var sayfalar: string[] = [];

    for (var i = 0; i < etiketler.length; i += SAYFA_BASINA)
        sayfalar.push(`<div class="sayfa">${etiketler.slice(i, i + SAYFA_BASINA).map(etiketHtml).join('')}</div>`);

    const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>QR Etiketleri</title><style>${stil}</style></head><body>${sayfalar.join('')}</body></html>`;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    const pencere = iframe.contentWindow;
    const belge = iframe.contentDocument ?? pencere?.document;

    if (!pencere || !belge) {
        document.body.removeChild(iframe);
        return;
    }

    belge.open();
    belge.write(html);
    belge.close();

    const kaldir = () => {
        if (iframe.parentNode)
            iframe.parentNode.removeChild(iframe);
    }

    // SVG satır içi olduğu için yüklenecek görsel yok; yazı tipinin oturması için kısa bekleme yeterli.
    setTimeout(() => {
        try {
            pencere.focus();
            pencere.print();
        } finally {
            // Chrome print() kapanana kadar bekletir; diğer tarayıcılar için gecikmeli temizlik.
            setTimeout(kaldir, 1000);
        }
    }, 250);
}
